import React, { useContext, useEffect, useState } from 'react';
import { AccountContext } from '../AccountContext';
import { toCurrency, toFormattedDate } from '@app/utilities';
import {
  useCurrentBillingPeriod,
  useGetBillingPeriod,
  useGetBillingPeriodBalance,
  useGetBillingPeriodEstimatedInterest,
  useGetInterestAssessmentForBillingPeriod,
} from '@app/sdk';
import { CopyToClipboardIcon } from '../../../shared/CopyToClipboardIcon';

export default function BillingPeriodSummary() {
  const { accountId, selectedBillingPeriod } = useContext(AccountContext);
  const [billingPeriod, setBillingPeriod] = useState({} as any);
  const { data: currentBillingPeriod } = useCurrentBillingPeriod(accountId);
  const [billingPeriodId, setBillingPeriodId] = useState<number | undefined>();
  const { data: billingPeriodById } = useGetBillingPeriod(accountId, billingPeriodId);
  const { data: billingPeriodBalance } = useGetBillingPeriodBalance({
    accountId,
    billingPeriodId: billingPeriodById?.billingPeriodId,
  });
  const { data: billingPeriodEstimatedInterest } = useGetBillingPeriodEstimatedInterest({
    accountId,
    billingPeriodId: billingPeriodById?.billingPeriodId,
  });
  const { data: interestAssessment } = useGetInterestAssessmentForBillingPeriod({
    accountId,
    billingPeriodId: billingPeriodById?.billingPeriodId,
  });
  useEffect(() => {
    if (selectedBillingPeriod && selectedBillingPeriod.billingPeriodId !== currentBillingPeriod?.billingPeriodId) {
      setBillingPeriodId(selectedBillingPeriod.billingPeriodId);
    }
  }, [currentBillingPeriod?.billingPeriodId, selectedBillingPeriod]);

  useEffect(() => {
    if (billingPeriodById && billingPeriodBalance) {
      const enrichedBillingPeriod = {
        ...billingPeriodById,
        openingBalance: billingPeriodBalance.openingStatementBalance.value,
        closingBalance: billingPeriodBalance.closingStatementBalance.value,
        closingSubledgerBalances: billingPeriodBalance.closingSubledgerBalances,
        estimatedInterest: billingPeriodEstimatedInterest?.estimatedInterest?.value,
      };
      setBillingPeriod(enrichedBillingPeriod);
    }
  }, [billingPeriodById, billingPeriodBalance, billingPeriodEstimatedInterest]);

  function getSubLedgerDue(subledgerId: string, subledgerBreakdowns: { id: string; amount: { value: number } }[]) {
    return subledgerBreakdowns.reduce((acc, curr) => {
      if (curr.id === subledgerId) {
        return (curr?.amount?.value ?? 0) + acc;
      }
      return acc;
    }, 0);
  }

  return (
    <>
      {selectedBillingPeriod?.billingPeriodId !== currentBillingPeriod?.billingPeriodId && (
        <>
          <h1>Billing Period {selectedBillingPeriod?.billingPeriodId} Summary</h1>
          {billingPeriod && (
            <table className="ui compact table">
              <thead>
                <tr>
                  <th>Billing Period</th>
                  <th>Payment Due Date</th>
                  <th>Payment Requested</th>
                  <th>Total Due</th>
                  <th>Dues</th>
                  <th>Instalments Total</th>
                  <th>Estimated Interest</th>
                  <th>Opening Balance</th>
                  <th>Closing Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="billingPeriodId">
                    Billing Period {billingPeriod.billingPeriodId}
                    <div className="ui grey sub header">
                      {toFormattedDate(billingPeriod?.startDate)} - {toFormattedDate(billingPeriod?.endDate)}
                    </div>
                  </td>
                  <td data-label="paymentDueDate">{toFormattedDate(billingPeriod?.paymentDueDate)}</td>
                  <td data-label="paymentRequested">{toCurrency(billingPeriod?.paymentRequested?.amount?.value)}</td>
                  <td data-label="totalDue">{toCurrency(billingPeriod?.paymentRequested?.minimumDue?.total?.value)}</td>
                  <td data-label="dues">
                    <div className="ui horizontal list">
                      <div className="ui label">
                        Current
                        <div className="detail">
                          {toCurrency(billingPeriod?.paymentRequested?.minimumDue.current.total.value)}
                        </div>
                      </div>
                      <div className="ui label">
                        Arrears
                        <div className="detail">
                          {toCurrency(
                            billingPeriod?.paymentRequested?.minimumDue.arrears
                              ? billingPeriod?.paymentRequested?.minimumDue.arrears?.total.value
                              : 0
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td data-label="instalmentsDue">
                    {toCurrency(billingPeriod?.instalmentPlans?.totalInstalmentDue.value || 0)}
                  </td>
                  <td data-label="estimatedInterest">{billingPeriod?.estimatedInterest ? toCurrency(billingPeriod?.estimatedInterest) : "N/A"}</td>
                  <td data-label="openingBalance">{toCurrency(billingPeriod?.openingBalance)}</td>
                  <td data-label="closingBalance">{toCurrency(billingPeriod?.closingBalance)}</td>
                </tr>
              </tbody>
            </table>
          )}

          <h2>Closing Subledger Balances</h2>
          {billingPeriod && (
            <table className="ui compact table">
              <thead>
                <tr>
                  <th>Subledger</th>
                  <th>Statemented</th>
                  <th>Dues</th>
                  <th>Total</th>
                  <th>Interest Assessment</th>
                </tr>
              </thead>
              <tbody>
                {billingPeriod.closingSubledgerBalances &&
                  Object.values(billingPeriod.closingSubledgerBalances).map((s: any) => (
                    <tr key={s.subledgerId}>
                      <td>
                        { s.subledgerType ? s.subledgerType : s.spendType}
                        <div className="ui grey sub header">{s.subledgerId}<CopyToClipboardIcon value={s.subledgerId} /></div>
                      </td>
                      <td>
                        <div className="ui horizontal list">
                          <div className="ui label">
                            Principal
                            <div className="detail">{toCurrency(s.balanceTypes.Principal.value)}</div>
                          </div>
                          <div className="ui label">
                            Interest
                            <div className="detail">{toCurrency(s.balanceTypes.Interest.value)}</div>
                          </div>
                        </div>
                      </td>
                      <td data-label="dues">
                        <div className="ui horizontal list">
                          <div className="ui label">
                            Current
                            <div className="detail">
                              {toCurrency(
                                getSubLedgerDue(
                                  s.subledgerId,
                                  billingPeriod.paymentRequested.minimumDue.current.subledgerBreakdowns
                                )
                              )}
                            </div>
                          </div>
                          <div className="ui label">
                            Arrears
                            <div className="detail">
                              {toCurrency(
                                getSubLedgerDue(
                                  s.subledgerId,
                                  billingPeriod?.paymentRequested?.minimumDue.arrears?.breakdown ?? []
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{toCurrency(s.total.value)}</td>
                      <td>
                        {interestAssessment &&
                          interestAssessment.subledgers
                            ?.find((a) => a.subledgerId == s.subledgerId)
                            ?.assessmentOutcomes.filter((a) => a?.amount.value > 0)
                            .map((a) => (
                              <div key={`${a.subledgerId}${a.type}${a.amount.value}${a.outcome}`} className="ui label">
                                {a.type} {a.outcome}
                                <div className="detail">{toCurrency(a.amount.value)}</div>
                              </div>
                            ))}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}
